import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  FiUsers, FiDownload, FiPrinter, FiRotateCcw, FiTrendingUp,
  FiTrendingDown, FiDollarSign, FiArrowUp, FiArrowDown
} from 'react-icons/fi';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { colors } from '../../theme/colors';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getEmployeesCached } from '../../utils/masterDataStore';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import { recordReads } from '../../utils/readTelemetry';
import { formatCurrency, formatNumber, formatDate } from '../../utils/formatters';
import useCurrency from '../../hooks/useCurrency';
import { addPrintBrand } from '../../utils/printBranding';

// ── Constants ──
const CHART_COLORS = ['#800020', '#A0153E', '#5D0017', '#C4A484', '#8B7355', '#D4AF37'];

const formatAmountLocal = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return dp > 0 ? '0.' + '0'.repeat(dp) : '0';
  return Number(amount).toLocaleString('en-PK', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  });
};

// ── Component ──

const PartySaleSummary = () => {
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  const taxLabel = activeCompany?.taxLabel || 'GST';
  const currencyCode = activeCompany?.currencyCode || 'PKR';
  const currencySymbol = activeCompany?.currencySymbol || 'Rs.';

  // Data states
  const [transactions, setTransactions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [areas, setAreas] = useState([]);
  const [regions, setRegions] = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter states
  const [selectedFYId, setSelectedFYId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState('all');
  const [selectedArea, setSelectedArea] = useState('all');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedSalesperson, setSelectedSalesperson] = useState('all');
  const [statusFilter, setStatusFilter] = useState('All');
  const [taxFilter, setTaxFilter] = useState('both');
  const [minAmount, setMinAmount] = useState('');

  // Sort states
  const [sortColumn, setSortColumn] = useState('netSales');
  const [sortDirection, setSortDirection] = useState('desc');

  // ── Load Financial Years ──
  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const fyRef = collection(db, 'companies', activeCompany.id, 'financialYears');
      const q = query(fyRef, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);

      if (activeFinancialYear?.id) {
        setSelectedFYId(activeFinancialYear.id);
      } else if (fys.length > 0) {
        setSelectedFYId(fys[0].id);
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
    }
  }, [activeCompany, activeFinancialYear]);

  // ── Load Customers ──
  const loadCustomers = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'customers');
      const q = query(ref, orderBy('companyName'));
      const snapshot = await getDocs(q);
      setCustomers(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  }, [activeCompany]);

  // ── Load Areas ──
  const loadAreas = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'areas');
      const q = query(ref, orderBy('areaName'));
      const snapshot = await getDocs(q);
      setAreas(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading areas:', error);
    }
  }, [activeCompany]);

  // ── Load Regions ──
  const loadRegions = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'regions');
      const q = query(ref, orderBy('regionName'));
      const snapshot = await getDocs(q);
      setRegions(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading regions:', error);
    }
  }, [activeCompany]);

  // ── Load Salespersons (from employees master — was a full sale-transactions scan) ──
  const loadSalespersons = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const empRows = await getEmployeesCached(activeCompany.id);
      const spList = empRows
        .map(data => {
          const name = (data.fullName || `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.employeeName || '').trim();
          return { id: data.id, employeeName: name };
        })
        .filter(sp => sp.employeeName);
      spList.sort((a, b) => a.employeeName.localeCompare(b.employeeName));
      setSalespersons(spList);
    } catch (error) {
      console.error('Error loading salespersons:', error);
    }
  }, [activeCompany]);

  // ── Load Sale Transactions ──
  const loadTransactions = useCallback(async () => {
    if (!activeCompany?.id || !selectedFYId) return;
    try {
      setLoading(true);
      const ref = collection(db, 'companies', activeCompany.id, 'transactions');

      const conditions = [
        where('tranTypeCode', 'in', ['SI', 'NSI', 'PS', 'SR', 'NSR']),
        where('financialYearId', '==', selectedFYId)
      ];
      // Server-side date bounds (Firebase cost): fetch only the picked range
      // instead of the whole FY. Empty pickers = whole FY (old behavior).
      // Client-side date filter below stays as a harmless second pass.
      if (dateFrom) conditions.push(where('docDate', '>=', dateFrom));
      if (dateTo) conditions.push(where('docDate', '<=', dateTo));

      const q = query(ref, ...conditions);
      const snapshot = await getDocs(q);
      recordReads(activeCompany.id, snapshot.size);
      const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(t => {
          if (!activeBranch || activeBranch.id === 'all') return true;
          return t.branchId === activeBranch.id;
        });
      data.sort((a, b) => (b.docDate || '').localeCompare(a.docDate || ''));
      setTransactions(data);
    } catch (error) {
      console.error('Error loading sale transactions:', error);
    } finally {
      setLoading(false);
    }
  }, [activeCompany, selectedFYId, activeBranch, dateFrom, dateTo]);

  useEffect(() => { loadFinancialYears(); }, [loadFinancialYears]);
  useEffect(() => { loadCustomers(); }, [loadCustomers]);
  useEffect(() => { loadAreas(); }, [loadAreas]);
  useEffect(() => { loadRegions(); }, [loadRegions]);
  useEffect(() => { loadSalespersons(); }, [loadSalespersons]);
  useEffect(() => {
    if (selectedFYId) loadTransactions();
  }, [loadTransactions, selectedFYId]);

  // ── Build customer lookup maps ──
  const customerMap = useMemo(() => {
    const map = {};
    customers.forEach(c => {
      map[c.id] = c;
    });
    return map;
  }, [customers]);

  const areaMap = useMemo(() => {
    const map = {};
    areas.forEach(a => {
      map[a.id] = a;
    });
    return map;
  }, [areas]);

  const regionMap = useMemo(() => {
    const map = {};
    regions.forEach(r => {
      map[r.id] = r;
    });
    return map;
  }, [regions]);

  // ── Filtered Transactions ──
  const filteredTransactions = useMemo(() => {
    let data = [...transactions];

    // Status filter: "Posted only" means only Posted; "All" means all statuses
    if (statusFilter === 'Posted') {
      data = data.filter(t => t.status === 'Posted');
    }

    if (dateFrom) {
      data = data.filter(t => (t.docDate || '') >= dateFrom);
    }
    if (dateTo) {
      data = data.filter(t => (t.docDate || '') <= dateTo);
    }
    if (selectedCustomer !== 'all') {
      data = data.filter(t => t.partyId === selectedCustomer);
    }
    if (selectedSalesperson !== 'all') {
      data = data.filter(t => t.employeeId === selectedSalesperson);
    }

    // Tax filter
    if (taxFilter === 'tax') {
      data = data.filter(t => !['NSI', 'NSR'].includes(t.tranTypeCode));
    } else if (taxFilter === 'nonTax') {
      data = data.filter(t => !['SI', 'SR'].includes(t.tranTypeCode));
    }

    // Area filter: match customer areaId
    if (selectedArea !== 'all') {
      data = data.filter(t => {
        const cust = customerMap[t.partyId];
        return cust && cust.areaId === selectedArea;
      });
    }

    // Region filter: match area regionId
    if (selectedRegion !== 'all') {
      const areasInRegion = new Set(
        areas.filter(a => a.regionId === selectedRegion).map(a => a.id)
      );
      data = data.filter(t => {
        const cust = customerMap[t.partyId];
        return cust && areasInRegion.has(cust.areaId);
      });
    }

    return data;
  }, [transactions, dateFrom, dateTo, selectedCustomer, selectedArea, selectedRegion, selectedSalesperson, statusFilter, taxFilter, customerMap, areas]);

  // ── Group by Party ──
  const partyData = useMemo(() => {
    const partyMap = {};

    filteredTransactions.forEach(t => {
      const pid = t.partyId || 'unknown';
      if (!partyMap[pid]) {
        const cust = customerMap[pid];
        const area = cust ? areaMap[cust.areaId] : null;
        partyMap[pid] = {
          partyId: pid,
          partyName: t.partyName || 'Unknown',
          customerCode: cust?.customerCode || '-',
          areaName: area?.areaName || '-',
          totalSales: 0,
          totalReturns: 0,
          netSales: 0,
          totalDiscount: 0,
          totalGST: 0,
          invoiceCount: 0
        };
      }

      const entry = partyMap[pid];
      const netAmt = t.netAmount || 0;
      const disc = t.totalDiscount || 0;
      const gst = t.totalGST || 0;

      if (['SI', 'NSI', 'PS'].includes(t.tranTypeCode)) {
        entry.totalSales += netAmt;
        entry.invoiceCount += 1;
        entry.totalDiscount += disc;
        entry.totalGST += gst;
      } else if (['SR', 'NSR'].includes(t.tranTypeCode)) {
        entry.totalReturns += netAmt;
        entry.totalDiscount -= disc;
        entry.totalGST -= gst;
      }
    });

    // Calculate netSales and avgInvoiceValue for each party
    Object.values(partyMap).forEach(entry => {
      entry.netSales = entry.totalSales - entry.totalReturns;
      entry.avgInvoiceValue = entry.invoiceCount > 0
        ? entry.totalSales / entry.invoiceCount
        : 0;
    });

    let result = Object.values(partyMap);

    // Minimum amount filter
    if (minAmount && !isNaN(parseFloat(minAmount))) {
      const threshold = parseFloat(minAmount);
      result = result.filter(p => p.netSales >= threshold);
    }

    // Sort
    result.sort((a, b) => b.netSales - a.netSales);

    return result;
  }, [filteredTransactions, customerMap, areaMap, minAmount]);

  // ── Grand Total for % of Total calculation ──
  const grandTotalNetSales = useMemo(() => {
    return partyData.reduce((sum, p) => sum + p.netSales, 0);
  }, [partyData]);

  // ── Sorted Data ──
  const sortedData = useMemo(() => {
    const data = [...partyData];
    data.sort((a, b) => {
      let valA, valB;
      switch (sortColumn) {
        case 'customerCode':
          valA = (a.customerCode || '').toLowerCase();
          valB = (b.customerCode || '').toLowerCase();
          break;
        case 'partyName':
          valA = (a.partyName || '').toLowerCase();
          valB = (b.partyName || '').toLowerCase();
          break;
        case 'areaName':
          valA = (a.areaName || '').toLowerCase();
          valB = (b.areaName || '').toLowerCase();
          break;
        case 'invoiceCount':
          valA = a.invoiceCount || 0;
          valB = b.invoiceCount || 0;
          break;
        case 'totalSales':
          valA = a.totalSales || 0;
          valB = b.totalSales || 0;
          break;
        case 'totalReturns':
          valA = a.totalReturns || 0;
          valB = b.totalReturns || 0;
          break;
        case 'totalDiscount':
          valA = a.totalDiscount || 0;
          valB = b.totalDiscount || 0;
          break;
        case 'totalGST':
          valA = a.totalGST || 0;
          valB = b.totalGST || 0;
          break;
        case 'netSales':
          valA = a.netSales || 0;
          valB = b.netSales || 0;
          break;
        case 'pctOfTotal':
          valA = grandTotalNetSales > 0 ? (a.netSales / grandTotalNetSales) : 0;
          valB = grandTotalNetSales > 0 ? (b.netSales / grandTotalNetSales) : 0;
          break;
        default:
          valA = a.netSales || 0;
          valB = b.netSales || 0;
      }
      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  }, [partyData, sortColumn, sortDirection, grandTotalNetSales]);

  // ── Summary Cards ──
  const summary = useMemo(() => {
    const totalCustomers = partyData.length;
    const totalSaleAmount = partyData.reduce((sum, p) => sum + p.totalSales, 0);
    const totalReturns = partyData.reduce((sum, p) => sum + p.totalReturns, 0);
    const netSales = totalSaleAmount - totalReturns;

    return {
      totalCustomers,
      totalSaleAmount,
      totalReturns,
      netSales
    };
  }, [partyData]);

  // ── Chart Data: Top 15 Customers Horizontal Bar ──
  const topCustomersBarData = useMemo(() => {
    return partyData
      .slice(0, 15)
      .map(p => ({
        customer: p.partyName.length > 20 ? p.partyName.substring(0, 20) + '...' : p.partyName,
        netSales: p.netSales
      }))
      .reverse(); // reversed so highest is at top of horizontal bar
  }, [partyData]);

  // ── Chart Data: Top 10 Customers Pie + Others ──
  const customerPieData = useMemo(() => {
    if (partyData.length === 0) return [];

    const top10 = partyData.slice(0, 10).map(p => ({
      name: p.partyName.length > 15 ? p.partyName.substring(0, 15) + '..' : p.partyName,
      value: p.netSales
    }));

    if (partyData.length > 10) {
      const othersTotal = partyData.slice(10).reduce((sum, p) => sum + p.netSales, 0);
      if (othersTotal > 0) {
        top10.push({ name: 'Others', value: othersTotal });
      }
    }

    return top10.filter(d => d.value > 0);
  }, [partyData]);

  // ── Table Totals ──
  const tableTotals = useMemo(() => {
    return {
      invoiceCount: sortedData.reduce((s, p) => s + p.invoiceCount, 0),
      totalSales: sortedData.reduce((s, p) => s + p.totalSales, 0),
      totalReturns: sortedData.reduce((s, p) => s + p.totalReturns, 0),
      totalDiscount: sortedData.reduce((s, p) => s + p.totalDiscount, 0),
      totalGST: sortedData.reduce((s, p) => s + p.totalGST, 0),
      netSales: sortedData.reduce((s, p) => s + p.netSales, 0)
    };
  }, [sortedData]);

  // ── Sort Handler ──
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection(column === 'partyName' || column === 'customerCode' || column === 'areaName' ? 'asc' : 'desc');
    }
  };

  const renderSortIcon = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc'
      ? <FiArrowUp size={11} style={{ marginLeft: 4 }} />
      : <FiArrowDown size={11} style={{ marginLeft: 4 }} />;
  };

  // ── Navigate to Sale Register filtered by customer ──
  const handleCustomerClick = (party) => {
    navigate('/reports/sale-register', {
      state: {
        filterCustomerId: party.partyId,
        filterCustomerName: party.partyName
      }
    });
  };

  // ── Reset Filters ──
  const handleResetFilters = () => {
    setDateFrom('');
    setDateTo('');
    setSelectedCustomer('all');
    setSelectedArea('all');
    setSelectedRegion('all');
    setSelectedSalesperson('all');
    setStatusFilter('All');
    setMinAmount('');
    if (activeFinancialYear?.id) {
      setSelectedFYId(activeFinancialYear.id);
    }
  };

  // ── CSV Export ──
  const handleExport = () => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    let csv = 'Party Sale Summary Report\n';
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || 'All'}\n`;
    csv += `Date Range: ${dateFrom || 'Start'} to ${dateTo || 'End'}\n`;
    csv += `Status: ${statusFilter}\n\n`;

    csv += `#,Customer Code,Customer Name,Area,Invoice Count,Gross Sales,Returns,Discount,${taxLabel},Net Sales,% of Total\n`;

    sortedData.forEach((p, idx) => {
      const pct = grandTotalNetSales > 0 ? ((p.netSales / grandTotalNetSales) * 100).toFixed(1) : '0.0';
      csv += `${idx + 1},`;
      csv += `"${(p.customerCode || '').replace(/"/g, '""')}",`;
      csv += `"${(p.partyName || '').replace(/"/g, '""')}",`;
      csv += `"${(p.areaName || '').replace(/"/g, '""')}",`;
      csv += `${p.invoiceCount},`;
      csv += `${p.totalSales.toFixed(2)},`;
      csv += `${p.totalReturns.toFixed(2)},`;
      csv += `${p.totalDiscount.toFixed(2)},`;
      csv += `${p.totalGST.toFixed(2)},`;
      csv += `${p.netSales.toFixed(2)},`;
      csv += `${pct}%\n`;
    });

    csv += `\nTOTAL,,,,`;
    csv += `${tableTotals.invoiceCount},`;
    csv += `${tableTotals.totalSales.toFixed(2)},`;
    csv += `${tableTotals.totalReturns.toFixed(2)},`;
    csv += `${tableTotals.totalDiscount.toFixed(2)},`;
    csv += `${tableTotals.totalGST.toFixed(2)},`;
    csv += `${tableTotals.netSales.toFixed(2)},`;
    csv += `100.0%\n`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Party_Sale_Summary_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // ── Print ──
  const handlePrint = () => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Party Sale Summary</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:13px}
        h1{color:#800020;font-size:20px;margin-bottom:5px}
        .meta{color:#666;font-size:12px;margin-bottom:20px}
        .summary-row{display:flex;gap:20px;margin-bottom:20px;flex-wrap:wrap}
        .summary-box{border:1px solid #ddd;border-radius:6px;padding:10px 16px;min-width:140px}
        .summary-box .label{font-size:11px;color:#666;text-transform:uppercase}
        .summary-box .value{font-size:18px;font-weight:600;color:#1A1A1A}
        table{width:100%;border-collapse:collapse;margin-top:10px}
        th{background:#800020;color:white;padding:8px 10px;text-align:left;font-size:11px;text-transform:uppercase}
        th.right{text-align:right}
        th.center{text-align:center}
        td{padding:6px 10px;border-bottom:1px solid #eee;font-size:12px}
        td.right{text-align:right;font-family:'Consolas',monospace}
        td.center{text-align:center}
        .totals-row{background:#f5f5f5;font-weight:700}
        .totals-row td{border-top:2px solid #800020;padding:10px}
        @media print{body{margin:15px}}
      </style></head><body>
      <h1>Party Sale Summary Report</h1>
      <div class="meta">
        ${activeCompany?.companyName || 'Company'} |
        Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || 'All'} |
        Date Range: ${dateFrom || 'Start'} to ${dateTo || 'End'} |
        Status: ${statusFilter} |
        Total Customers: ${sortedData.length}
      </div>
      <div class="summary-row">
        <div class="summary-box"><div class="label">Customers with Sales</div><div class="value">${formatNumber(summary.totalCustomers)}</div></div>
        <div class="summary-box"><div class="label">Total Sale Amount</div><div class="value">${currencySymbol}${formatAmountLocal(summary.totalSaleAmount)}</div></div>
        <div class="summary-box"><div class="label">Total Returns</div><div class="value">${currencySymbol}${formatAmountLocal(summary.totalReturns)}</div></div>
        <div class="summary-box"><div class="label">Net Sales</div><div class="value">${currencySymbol}${formatAmountLocal(summary.netSales)}</div></div>
      </div>
      <table>
        <thead><tr>
          <th>#</th><th>Code</th><th>Customer Name</th><th>Area</th>
          <th class="right">Invoices</th><th class="right">Gross Sales</th><th class="right">Returns</th>
          <th class="right">Discount</th><th class="right">${taxLabel}</th><th class="right">Net Sales</th><th class="right">% of Total</th>
        </tr></thead>
        <tbody>`);

    sortedData.forEach((p, idx) => {
      const pct = grandTotalNetSales > 0 ? ((p.netSales / grandTotalNetSales) * 100).toFixed(1) : '0.0';
      w.document.write(`
        <tr>
          <td>${idx + 1}</td>
          <td>${p.customerCode || '-'}</td>
          <td>${p.partyName || ''}</td>
          <td>${p.areaName || '-'}</td>
          <td class="right">${p.invoiceCount}</td>
          <td class="right">${formatAmountLocal(p.totalSales)}</td>
          <td class="right">${formatAmountLocal(p.totalReturns)}</td>
          <td class="right">${formatAmountLocal(p.totalDiscount)}</td>
          <td class="right">${formatAmountLocal(p.totalGST)}</td>
          <td class="right">${formatAmountLocal(p.netSales)}</td>
          <td class="right">${pct}%</td>
        </tr>`);
    });

    w.document.write(`
        <tr class="totals-row">
          <td colspan="4" style="text-align:right">TOTAL (${sortedData.length} customers)</td>
          <td class="right">${tableTotals.invoiceCount}</td>
          <td class="right">${formatAmountLocal(tableTotals.totalSales)}</td>
          <td class="right">${formatAmountLocal(tableTotals.totalReturns)}</td>
          <td class="right">${formatAmountLocal(tableTotals.totalDiscount)}</td>
          <td class="right">${formatAmountLocal(tableTotals.totalGST)}</td>
          <td class="right">${formatAmountLocal(tableTotals.netSales)}</td>
          <td class="right">100.0%</td>
        </tr>
        </tbody></table>
        <div style="margin-top:30px;text-align:center;color:#999;font-size:11px">
          Generated on ${new Date().toLocaleString()} | AMAL ERP
        </div>
      </body></html>`);
    addPrintBrand(w);
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  // ── Custom Tooltip for Charts ──
  const CustomBarTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <ChartTooltipWrapper>
        <ChartTooltipLabel>{label}</ChartTooltipLabel>
        {payload.map((entry, i) => (
          <ChartTooltipRow key={i}>
            <ChartTooltipDot style={{ background: entry.color }} />
            <span>{entry.name}:</span>
            <strong>{formatCurrency(entry.value)}</strong>
          </ChartTooltipRow>
        ))}
      </ChartTooltipWrapper>
    );
  };

  const PieCustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const data = payload[0];
    const total = customerPieData.reduce((s, d) => s + d.value, 0);
    const pct = total > 0 ? ((data.value / total) * 100).toFixed(1) : 0;
    return (
      <ChartTooltipWrapper>
        <ChartTooltipLabel>{data.name}</ChartTooltipLabel>
        <ChartTooltipRow>
          <span>Amount:</span>
          <strong>{formatCurrency(data.value)}</strong>
        </ChartTooltipRow>
        <ChartTooltipRow>
          <span>Share:</span>
          <strong>{pct}%</strong>
        </ChartTooltipRow>
      </ChartTooltipWrapper>
    );
  };

  // ── Render ──
  return (
    <PageLayout pageTitle="Party Sale Summary" breadcrumb="Reports / Sale Reports / Party Sale Summary">
      {/* Summary Cards */}
      <CardsGrid>
        <SummaryCard>
          <CardIcon color={colors.primary.main}>
            <FiUsers />
          </CardIcon>
          <CardInfo>
            <CardLabel>Customers with Sales</CardLabel>
            <CardValue>{formatNumber(summary.totalCustomers)}</CardValue>
            <CardSub>Active customers</CardSub>
          </CardInfo>
        </SummaryCard>

        <SummaryCard>
          <CardIcon color={colors.success.main}>
            <FiDollarSign />
          </CardIcon>
          <CardInfo>
            <CardLabel>Total Sale Amount</CardLabel>
            <CardValue>{formatCurrency(summary.totalSaleAmount)}</CardValue>
            <CardSub>Gross invoiced</CardSub>
          </CardInfo>
        </SummaryCard>

        <SummaryCard>
          <CardIcon color={colors.error.main}>
            <FiTrendingDown />
          </CardIcon>
          <CardInfo>
            <CardLabel>Total Returns</CardLabel>
            <CardValue>{formatCurrency(summary.totalReturns)}</CardValue>
            <CardSub>Sale returns</CardSub>
          </CardInfo>
        </SummaryCard>

        <SummaryCard>
          <CardIcon color={colors.info.main}>
            <FiTrendingUp />
          </CardIcon>
          <CardInfo>
            <CardLabel>Net Sales</CardLabel>
            <CardValue>{formatCurrency(summary.netSales)}</CardValue>
            <CardSub>Sales - Returns</CardSub>
          </CardInfo>
        </SummaryCard>
      </CardsGrid>

      {/* Filters */}
      <FiltersCard>
        <FiltersRow>
          <FilterGroup>
            <Label>Financial Year</Label>
            <Select
              value={selectedFYId}
              onChange={(e) => setSelectedFYId(e.target.value)}
            >
              <option value="">-- Select Year --</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>
                  {fy.yearName || fy.yearCode}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Date From</Label>
            <DateInput
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Date To</Label>
            <DateInput
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Customer</Label>
            <Select
              value={selectedCustomer}
              onChange={(e) => setSelectedCustomer(e.target.value)}
            >
              <option value="all">All Customers</option>
              {customers.map(c => (
                <option key={c.id} value={c.id}>
                  {c.companyName}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Area</Label>
            <Select
              value={selectedArea}
              onChange={(e) => setSelectedArea(e.target.value)}
            >
              <option value="all">All Areas</option>
              {areas.map(a => (
                <option key={a.id} value={a.id}>
                  {a.areaName}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Region</Label>
            <Select
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
            >
              <option value="all">All Regions</option>
              {regions.map(r => (
                <option key={r.id} value={r.id}>
                  {r.regionName}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Salesperson</Label>
            <Select
              value={selectedSalesperson}
              onChange={(e) => setSelectedSalesperson(e.target.value)}
            >
              <option value="all">All Salespersons</option>
              {salespersons.map(sp => (
                <option key={sp.id} value={sp.id}>
                  {sp.employeeName}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Status</Label>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="All">All</option>
              <option value="Posted">Posted Only</option>
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Tax Filter</Label>
            <Select
              value={taxFilter}
              onChange={(e) => setTaxFilter(e.target.value)}
            >
              <option value="both">Both (Tax + Non-Tax)</option>
              <option value="tax">Tax Only</option>
              <option value="nonTax">Non-Tax Only</option>
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Min Amount</Label>
            <NumberInput
              type="number"
              placeholder="0"
              value={minAmount}
              onChange={(e) => setMinAmount(e.target.value)}
            />
          </FilterGroup>

          <ButtonGroup>
            <Button onClick={handleResetFilters} title="Reset Filters">
              <FiRotateCcw /> Reset
            </Button>
            <Button onClick={handleExport}>
              <FiDownload /> CSV
            </Button>
            <Button onClick={handlePrint}>
              <FiPrinter /> Print
            </Button>
          </ButtonGroup>
        </FiltersRow>
      </FiltersCard>

      {/* Data Table */}
      <ContentCard>
        <TableHeader>
          <TableHeaderTitle>
            Party Sale Summary
            <TableCount>{sortedData.length} customer{sortedData.length !== 1 ? 's' : ''}</TableCount>
          </TableHeaderTitle>
        </TableHeader>
        <TableContainer>
          {loading ? (
            <EmptyState>
              <LoadingSpinner />
              <EmptyText>Loading party sale data...</EmptyText>
            </EmptyState>
          ) : sortedData.length === 0 ? (
            <EmptyState>
              <EmptyIcon><FiUsers /></EmptyIcon>
              <EmptyText>
                {transactions.length === 0
                  ? 'No sale transactions found for this financial year'
                  : 'No customers match the selected filters'}
              </EmptyText>
              <EmptySubText>Try adjusting your filters or selecting a different financial year</EmptySubText>
            </EmptyState>
          ) : (
            <Table>
              <Thead>
                <tr>
                  <Th>#</Th>
                  <Th sortable onClick={() => handleSort('customerCode')}>
                    Code {renderSortIcon('customerCode')}
                  </Th>
                  <Th sortable onClick={() => handleSort('partyName')}>
                    Customer Name {renderSortIcon('partyName')}
                  </Th>
                  <Th sortable onClick={() => handleSort('areaName')}>
                    Area {renderSortIcon('areaName')}
                  </Th>
                  <Th align="right" sortable onClick={() => handleSort('invoiceCount')}>
                    Invoices {renderSortIcon('invoiceCount')}
                  </Th>
                  <Th align="right" sortable onClick={() => handleSort('totalSales')}>
                    Gross Sales {renderSortIcon('totalSales')}
                  </Th>
                  <Th align="right" sortable onClick={() => handleSort('totalReturns')}>
                    Returns {renderSortIcon('totalReturns')}
                  </Th>
                  <Th align="right" sortable onClick={() => handleSort('totalDiscount')}>
                    Discount {renderSortIcon('totalDiscount')}
                  </Th>
                  <Th align="right" sortable onClick={() => handleSort('totalGST')}>
                    {taxLabel} {renderSortIcon('totalGST')}
                  </Th>
                  <Th align="right" sortable onClick={() => handleSort('netSales')}>
                    Net Sales {renderSortIcon('netSales')}
                  </Th>
                  <Th align="right" sortable onClick={() => handleSort('pctOfTotal')}>
                    % of Total {renderSortIcon('pctOfTotal')}
                  </Th>
                </tr>
              </Thead>
              <Tbody>
                {sortedData.map((p, idx) => {
                  const pct = grandTotalNetSales > 0
                    ? ((p.netSales / grandTotalNetSales) * 100).toFixed(1)
                    : '0.0';
                  return (
                    <Tr key={p.partyId}>
                      <Td>{idx + 1}</Td>
                      <Td>
                        <CodeText>{p.customerCode || '-'}</CodeText>
                      </Td>
                      <Td title={p.partyName}>
                        <CustomerLink onClick={() => handleCustomerClick(p)}>
                          {p.partyName}
                        </CustomerLink>
                      </Td>
                      <Td>{p.areaName || '-'}</Td>
                      <AmountTd align="right">{formatNumber(p.invoiceCount)}</AmountTd>
                      <AmountTd align="right">{formatAmountLocal(p.totalSales)}</AmountTd>
                      <AmountTd align="right" $negative={p.totalReturns > 0}>
                        {formatAmountLocal(p.totalReturns)}
                      </AmountTd>
                      <AmountTd align="right">{formatAmountLocal(p.totalDiscount)}</AmountTd>
                      <AmountTd align="right">{formatAmountLocal(p.totalGST)}</AmountTd>
                      <AmountTd align="right" $highlight>
                        {formatAmountLocal(p.netSales)}
                      </AmountTd>
                      <Td align="right">
                        <PctBar>
                          <PctBarFill style={{ width: `${Math.min(parseFloat(pct), 100)}%` }} />
                          <PctText>{pct}%</PctText>
                        </PctBar>
                      </Td>
                    </Tr>
                  );
                })}

                {/* Totals Row */}
                <TotalsRow>
                  <Td colSpan={4} style={{ textAlign: 'right', fontWeight: 700 }}>
                    TOTAL ({sortedData.length} customers)
                  </Td>
                  <AmountTd align="right">{formatNumber(tableTotals.invoiceCount)}</AmountTd>
                  <AmountTd align="right">{formatAmountLocal(tableTotals.totalSales)}</AmountTd>
                  <AmountTd align="right">{formatAmountLocal(tableTotals.totalReturns)}</AmountTd>
                  <AmountTd align="right">{formatAmountLocal(tableTotals.totalDiscount)}</AmountTd>
                  <AmountTd align="right">{formatAmountLocal(tableTotals.totalGST)}</AmountTd>
                  <AmountTd align="right" $highlight>{formatAmountLocal(tableTotals.netSales)}</AmountTd>
                  <Td align="right" style={{ fontWeight: 700, fontFamily: "'SF Mono', 'Consolas', monospace", fontSize: 12 }}>
                    100.0%
                  </Td>
                </TotalsRow>
              </Tbody>
            </Table>
          )}
        </TableContainer>
      </ContentCard>

      {/* Charts */}
      {!loading && sortedData.length > 0 && (
        <ChartsGrid>
          {/* Horizontal Bar Chart: Top 15 Customers by Net Sales */}
          <ChartCard>
            <ChartTitle>Top 15 Customers by Net Sales</ChartTitle>
            {topCustomersBarData.length > 0 ? (
              <ResponsiveContainer width="100%" height={420}>
                <BarChart data={topCustomersBarData} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E9ECEF" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 10, fill: colors.text.secondary }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => formatCurrency(v, { compact: true })}
                  />
                  <YAxis
                    type="category"
                    dataKey="customer"
                    tick={{ fontSize: 10, fill: colors.text.secondary }}
                    axisLine={false}
                    tickLine={false}
                    width={150}
                  />
                  <Tooltip content={<CustomBarTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="netSales" name="Net Sales" fill="#800020" radius={[0, 4, 4, 0]} barSize={18} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No customer data available</ChartEmpty>
            )}
          </ChartCard>

          {/* Pie Chart: Sales Distribution by Top 10 Customers + Others */}
          <ChartCard>
            <ChartTitle>Sales Distribution by Customer</ChartTitle>
            {customerPieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={420}>
                <PieChart>
                  <Pie
                    data={customerPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={120}
                    paddingAngle={3}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) =>
                      `${name.length > 14 ? name.substring(0, 14) + '..' : name} (${(percent * 100).toFixed(0)}%)`
                    }
                    labelLine={{ stroke: colors.text.secondary, strokeWidth: 1 }}
                  >
                    {customerPieData.map((_, idx) => (
                      <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieCustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No customer data available</ChartEmpty>
            )}
          </ChartCard>
        </ChartsGrid>
      )}
    </PageLayout>
  );
};

// ══════════════════════════════════════════════════════════════
//  Styled Components
// ══════════════════════════════════════════════════════════════

// ── Summary Cards ──

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    box-shadow: ${colors.shadows.md};
    transform: translateY(-1px);
  }
`;

const CardIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.color || colors.primary.main}15;
  color: ${props => props.color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const CardInfo = styled.div`
  min-width: 0;
  flex: 1;
`;

const CardLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
  text-transform: uppercase;
  font-weight: 600;
`;

const CardValue = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: ${colors.text.primary};
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardSub = styled.div`
  font-size: 11px;
  color: ${colors.text.tertiary};
  margin-top: 2px;
`;

// ── Filters ──

const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: flex-end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 160px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 0;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const DateInput = styled.input`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  transition: all 0.2s;
  min-width: 140px;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const NumberInput = styled.input`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  transition: all 0.2s;
  min-width: 100px;
  max-width: 140px;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    opacity: 1;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`;

const Button = styled.button`
  padding: 9px 16px;
  border: none;
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  background: ${colors.neutral[100]};
  color: ${colors.text.primary};
  white-space: nowrap;

  &:hover {
    background: ${colors.neutral[200]};
  }
`;

// ── Charts ──

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
`;

const ChartTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 16px;
`;

const ChartEmpty = styled.div`
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.text.tertiary};
  font-size: 14px;
`;

const ChartTooltipWrapper = styled.div`
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${colors.borderRadius.md};
  box-shadow: ${colors.shadows.md};
  padding: 10px 14px;
  font-size: 12px;
`;

const ChartTooltipLabel = styled.div`
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 6px;
  font-size: 12px;
`;

const ChartTooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${colors.text.secondary};
  margin-bottom: 2px;

  strong {
    color: ${colors.text.primary};
    margin-left: auto;
  }
`;

const ChartTooltipDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
`;

// ── Table ──

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid ${colors.neutral[100]};
`;

const TableHeaderTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.text.primary};
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TableCount = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${colors.text.secondary};
  background: ${colors.neutral[100]};
  padding: 3px 10px;
  border-radius: 12px;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 11px 14px;
  text-align: ${props => props.align || 'left'};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid ${colors.neutral[200]};
  white-space: nowrap;
  user-select: none;

  ${props => props.sortable && `
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: ${colors.primary.main};
    }
  `}
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background 0.15s;

  &:hover {
    background: ${colors.neutral[50]};
  }
`;

const TotalsRow = styled.tr`
  background: ${colors.primary.main}08;
  border-top: 2px solid ${colors.primary.main};

  td {
    font-weight: 700 !important;
    font-size: 13px !important;
    padding: 12px 14px !important;
  }
`;

const Td = styled.td`
  padding: 10px 14px;
  font-size: 13px;
  color: ${colors.text.primary};
  text-align: ${props => props.align || 'left'};
  white-space: nowrap;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AmountTd = styled(Td)`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 12px;
  color: ${props => props.$negative ? colors.error.main : props.$highlight ? colors.text.primary : colors.text.secondary};
  font-weight: ${props => props.$highlight ? 600 : 400};
`;

const CodeText = styled.span`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 12px;
  color: ${colors.text.secondary};
`;

const CustomerLink = styled.span`
  color: ${colors.primary.main};
  font-weight: 600;
  cursor: pointer;
  font-size: 13px;
  transition: color 0.2s;

  &:hover {
    color: ${colors.primary.dark};
    text-decoration: underline;
  }
`;

const PctBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 100px;
  justify-content: flex-end;
`;

const PctBarFill = styled.div`
  height: 6px;
  background: ${colors.primary.main}30;
  border-radius: 3px;
  min-width: 0;
  flex: 1;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background: ${colors.primary.main};
    border-radius: 3px;
  }
`;

const PctText = styled.span`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  min-width: 42px;
  text-align: right;
`;

// ── Empty / Loading ──

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.neutral[300]};
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 6px;
  color: ${colors.text.primary};
`;

const EmptySubText = styled.p`
  font-size: 13px;
  color: ${colors.text.tertiary};
`;

const LoadingSpinner = styled.div`
  width: 36px;
  height: 36px;
  border: 3px solid ${colors.neutral[200]};
  border-top: 3px solid ${colors.primary.main};
  border-radius: 50%;
  margin: 0 auto 16px auto;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

export default PartySaleSummary;
